"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  {
    from: "Pending",
    to: "Approved (or cheque steps first), Rejected",
  },
  {
    from: "Approved, Rejected",
    to: "Cancelled",
  },
  {
    from: "Cancelled",
    to: "Nothing. Cancelled is a dead end",
  },
];

const CHEQUE_STEPS = [
  {
    from: "Cheque Pending",
    to: "Receive Cheque Approval → Received. Payment stays Pending",
  },
  {
    from: "Cheque Received",
    to: "Deposit Cheque → Deposited. Payment stays Pending",
  },
  {
    from: "Cheque Deposited",
    to: "Approve → cheque Cleared and payment Approved. Balances and ledger move here",
  },
];

const APPLY_FROM = [
  {
    from: "Invoice",
    when: "Sent, Partially Paid, or Overdue, with a balance greater than zero, same customer and branch",
    result:
      "You pick the invoice on Add Payment Received and enter how much of this receipt to apply. Approving the payment moves the invoice to Partially Paid or Paid",
  },
  {
    from: "Customer opening balance",
    when: "That customer still has remaining opening receivable on this branch",
    result:
      "The allocation table includes a line labelled (Customer opening balance). Enter how much of this receipt to put on it, same as an invoice line. Approve reduces that remaining amount",
  },
  {
    from: "Scratch / advance",
    when: "Advanced Payment is ticked, or you receive more than you allocate",
    result:
      "The leftover sits as Unused Amount. After Approve you can Apply to Invoice, Auto Pay Invoices, or Refund it",
  },
];

const AFTER_APPROVE = [
  {
    to: "Invoice / opening receivable",
    when: "Approved and Unused Amount is greater than zero",
    where: "Apply to Invoice on the list or detail, or Auto Pay Invoices on the list",
    result:
      "Uses unused money against open invoices or remaining positive opening balance. Does not create a second payment",
  },
  {
    to: "Refund",
    when: "Approved and Unused Amount is greater than zero",
    where: "Refund on the list or detail",
    result: "Returns unused money. Unused Amount goes down",
  },
];

const RULES = [
  {
    topic: "Ledger posts on Approve, not on Save",
    rule: "Save creates a Pending payment. Nothing hits the bank/cash account, Accounts Receivable, or Unearned Revenue until an Accountant Approves. Cancel or delete of an Approved payment reverses those journals",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a payment is limited by your plan. Editing, approve, apply, refund, and deleting are not separately gated",
  },
  {
    topic: "No primary bank requirement",
    rule: "Unlike Quote, Sales Order, Delivery Challan, and Invoice, you do not need a primary bank to create a payment. You must pick a Deposit To account (Cash, Bank, Payment Clearing, or Credit Card on the chart of accounts)",
  },
  {
    topic: "Payment number",
    rule: "Auto-generated with a PMT prefix unless you turn that off under Payment Received Configuration on the form. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Customer, Deposit To",
    rule: "All required to create. Customer and location stay locked after save. You can only edit a Pending payment",
  },
  {
    topic: "Currency",
    rule: "Taken from the invoices or opening-balance line you apply to, or from the customer on an advance. Cross-currency receipts are blocked. An exchange-rate field appears when the document currency differs from the company base. Ledger posting on Approve runs when the payment is in base currency",
  },
  {
    topic: "Payment modes",
    rule: "Petty Cash, Cheque, Bank Transfer, or UPI. Cheque is the only mode with extra dates and a three-step approve path before the payment itself becomes Approved",
  },
  {
    topic: "Approve and Reject are Accountant-only",
    rule: "Mark Payment As on a Pending payment is offered to an Accountant with Edit. Rejected payments can still be Cancelled. There is no Draft status",
  },
  {
    topic: "Unused Amount",
    rule: "Received Amount minus what you allocated on this payment. After Approve you can Apply to Invoice (including remaining opening balance) or Refund. It is not the Unused Credit figure on the customer list",
  },
  {
    topic: "Advance already applied cannot cancel or delete",
    rule: "Unapply the invoice or opening-balance line first (Remove on an advance payment). Then cancel or delete is allowed",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a payment received" },
      { heading: "2. Against invoices, advance, and unused amount" },
      { heading: "3. Paying against a customer opening balance" },
      { heading: "4. Viewing, searching, and exporting" },
      { heading: "5. Editing a payment" },
      { heading: "6. Changing status — including cheque steps" },
      { heading: "7. After Approve — apply unused, auto pay, refund" },
      { heading: "8. Cancelling and deleting" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function PaymentsReceivedPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Payments Received
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording money a customer paid you, then matching it
            to invoices or to what they already owed on opening balance
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Payment Received</B> is money in — against open invoices, as
            an advance, or against remaining customer opening balance on the
            same form. Save makes it <B>Pending</B>. An Accountant{" "}
            <B>Approves</B> it before the ledger, the deposit account, and
            invoice / opening-balance remaining amounts move.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a payment received
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            those three can pre-fill from{" "}
            <B>Settings → Organization defaults</B>. Then customer, amount,
            and where the money landed.
          </p>
          <Definitions
            items={[
              {
                term: "Payment Number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Payment Received Configuration</B> on the form (prefix
                    and next number, default PMT). Locked after save.
                  </>
                ),
              },
              {
                term: "Customer and Payment Date",
                desc: (
                  <>
                    Who paid, and when. After save you cannot change the
                    customer.
                  </>
                ),
              },
              {
                term: "Deposit To",
                desc: (
                  <>
                    Required. A chart-of-accounts account in Cash, Bank,
                    Payment Clearing, or Credit Card — not the Banks master
                    picker, and not a primary-bank check.
                  </>
                ),
              },
              {
                term: "Payment Mode",
                desc: (
                  <>
                    <B>Petty Cash</B>, <B>Cheque</B>, <B>Bank Transfer</B>, or{" "}
                    <B>UPI</B>. Cheque also requires a Received Date, and
                    optional Cheque No.
                  </>
                ),
              },
              {
                term: "Received Amount",
                desc: <>Must be greater than zero.</>,
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Follows the invoices or opening-balance line you apply, or
                    the customer on an advance. If it differs from the company
                    base (<B>Settings → Currencies</B>), an{" "}
                    <B>Exchange Rate</B> field appears. You cannot mix
                    currencies on one receipt.
                  </>
                ),
              },
              {
                term: "Tax deducted / TDS",
                desc: (
                  <>
                    Optional. If you mark Yes, TDS (Income Tax), you also pick
                    a TDS Tax Account.
                  </>
                ),
              },
              {
                term: "Save",
                desc: (
                  <>
                    There is no Save &amp; Approve. Save always creates{" "}
                    <B>Pending</B>. Balances do not move yet.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Unlike an Invoice, a payment does <B>not</B> post to the ledger
            when you save. Posting waits for Approve.
          </Callout>
          <Screenshot caption="Screenshot: Add Payment Received, showing Division, Branch, Warehouse, Customer, Deposit To, Payment Mode, Received Amount, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Against invoices, advance, and unused amount
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            You either allocate this receipt now, or hold it as unused money
            to apply later.
          </p>
          <Definitions
            items={[
              {
                term: "Against invoices",
                desc: (
                  <>
                    The picker lists that customer&apos;s{" "}
                    <B>Sent</B>, <B>Partially Paid</B>, and <B>Overdue</B>{" "}
                    invoices with a remaining balance, plus any{" "}
                    <B>(Customer opening balance)</B> receivable line. You
                    enter how much of this receipt to put on each line. The
                    total applied cannot exceed Received Amount.
                  </>
                ),
              },
              {
                term: "Advanced Payment",
                desc: (
                  <>
                    Tick this and skip invoice lines. The full amount becomes
                    Unused Amount after Approve.
                  </>
                ),
              },
              {
                term: "Unused Amount",
                desc: (
                  <>
                    Received Amount minus what you allocated. After Approve you
                    can Apply to Invoice, Auto Pay Invoices, or Refund.
                  </>
                ),
              },
            ]}
          />
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4 mt-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    You start from
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What this payment does
                  </th>
                </tr>
              </thead>
              <tbody>
                {APPLY_FROM.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < APPLY_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: the invoice allocation table on Add Payment Received, including a (Customer opening balance) line and Unused Amount." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Paying against a customer opening balance
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Remaining opening balance that this customer still owes on the
            selected branch appears on the same allocation table as invoices.
            It is not a separate payment type.
          </p>
          <Definitions
            items={[
              {
                term: "(Customer opening balance) line",
                desc: (
                  <>
                    After you pick the customer, this line shows if that branch
                    still has opening receivable left. Enter{" "}
                    <B>amount applied</B> on it the same way you do for an
                    invoice.
                  </>
                ),
              },
              {
                term: "What Approve does",
                desc: (
                  <>
                    Remaining opening balance on that branch goes down by the
                    amount applied. Current Balance moves with it. The payment
                    stays one Payment Received — there is no extra document.
                  </>
                ),
              },
              {
                term: "Unused leftover",
                desc: (
                  <>
                    If Received Amount is more than the opening-balance line
                    plus invoices, the rest is Unused Amount. After Approve,
                    Apply to Invoice / Auto Pay can put that leftover on more
                    invoices or remaining opening balance.
                  </>
                ),
              },
              {
                term: "Unapply",
                desc: (
                  <>
                    On an advance, you can remove the opening-balance line.
                    Unused Amount comes back. Do that before cancel or delete
                    if the line was applied.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Payment Received allocation table with a (Customer opening balance) line and amount applied." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list filters by status, including payments that still have
            unused money to apply.
          </p>
          <Definitions
            items={[
              {
                term: "Status tabs",
                desc: (
                  <>
                    Pending, Approved, Rejected, Cancelled. A pending-mapping
                    filter shows Approved payments that still have Unused
                    Amount.
                  </>
                ),
              },
              {
                term: "Detail",
                desc: (
                  <>
                    Deposit To, invoices applied, opening-balance lines,
                    unused amount, cheque status when the mode is Cheque, and
                    history.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Payments Received list with status tabs and Unused Amount." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a payment
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            You can only edit <B>Pending</B>. Approved, Rejected, and
            Cancelled cannot be edited. Customer and location stay locked even
            on Pending — change amounts and allocations, not who paid.
          </p>
          <Screenshot caption="Screenshot: Edit on a Pending payment received." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status — including cheque steps
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            There is no Draft. <B>Mark Payment As</B> on Pending is for an
            Accountant with Edit.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    You can move to
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {row.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            If Payment Mode is <B>Cheque</B>, Approve is three clicks. The
            payment stays Pending until the last one.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Cheque status now
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What Approve does
                  </th>
                </tr>
              </thead>
              <tbody>
                {CHEQUE_STEPS.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < CHEQUE_STEPS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {row.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            On Approve the deposit account is debited for the full receipt.
            Applied amount credits Accounts Receivable (invoices and positive
            opening balance). Unused amount credits Unearned Revenue until you
            apply or refund it.
          </Callout>
          <Screenshot caption="Screenshot: Mark Payment As on a Pending payment, and Receive Cheque Approval on a cheque payment." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. After Approve — apply unused, auto pay, refund
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            These run on an <B>Approved</B> payment that still has Unused
            Amount. They do not create another payment.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens
                  </th>
                </tr>
              </thead>
              <tbody>
                {AFTER_APPROVE.map((row, i) => (
                  <tr
                    key={row.to}
                    className={
                      i < AFTER_APPROVE.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[160px]">
                      {row.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Remove / unapply",
                desc: (
                  <>
                    On an advance, you can remove an invoice or an
                    opening-balance line. Unused Amount comes back. Do this
                    before cancel or delete if anything was applied.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Apply to Invoice, Auto Pay Invoices, and Refund on an Approved payment with unused amount." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Cancelling and deleting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Cancel leaves the record as Cancelled. Delete removes it.
          </p>
          <Definitions
            items={[
              {
                term: "Cancel",
                desc: (
                  <>
                    Offered on <B>Approved</B> or <B>Rejected</B> to an
                    Accountant. Blocked if this payment (as an advance) still
                    has invoices or opening-balance lines applied — unapply
                    first. If it was Approved, invoice remaining amounts, OB
                    remaining, branch current balance, and journals reverse.
                  </>
                ),
              },
              {
                term: "Delete",
                desc: (
                  <>
                    Pending or Approved, with the same applied-advance block.
                    Same reversals as cancel if it had been Approved, then the
                    record is gone.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Cancel blocked because the advance was already applied to an invoice." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Sent / Partially Paid / Overdue invoices this payment can settle
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Opening Balance is set on the customer branch
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Deposit To accounts, AR, and Unearned Revenue
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
